import { Controller } from "@hotwired/stimulus";
import Tribute from "tributejs";

function debounce(func, wait) {
    let timeout;
    return function (...args) {
        const context = this;
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(context, args), wait);
    };
}

export default class extends Controller {
    static get values() {
        return {
            searchMentionablesUrl: String,
            values: Array,
            searchTagsUrl: String
        };
    }

    static get targets() {
        return ["newImageFile", "form", "message", "messageHtml", "mentionedTeamIds", "mentionedUserIds", "valueIds", "tags", "imageUrl", "staticImageUrl", "imageFile", "isPoll", "messageEditor", "imageContainer", "image", "pollContainer", "toolbarButton", "addAnEmojiButton", "emojiPicker", "addAGifButton", "gifDisplayContainer", "removeGifContainer", "gifSearch", "gifResults", "addAnImageButton", "addAPollButton"];
    }

    async connect() {
        this.toolbarButtonTargets.forEach(tbt => new bootstrap.Tooltip(tbt));
        const { data, Picker } = await import("imports/emoji-mart");
        const picker = new Picker({
            data,
            onEmojiSelect: (e) => {
                this.insertEmoji(e.native);
            }
        });
        this.emojiPickerTarget.appendChild(picker);
        this.searchGifs = debounce(this.searchGifs.bind(this), 300);
    }

    async messageEditorTargetConnected(target) {
        const tributeCollection = [];

        if (this.hasSearchMentionablesUrlValue) {
            tributeCollection.push({
                trigger: "@",
                values: async (text, callback) => {
                    const urlSearchParams = new URLSearchParams();
                    urlSearchParams.append("searchText", text);
                    urlSearchParams.append("page", 1)
                    urlSearchParams.append("pageSize", 10);
                    const response = await fetch(`${this.searchMentionablesUrlValue}?${urlSearchParams}`);
                    callback(await response.json());
                },
                lookup: "text",
                menuItemTemplate: function (item) {
                    return `<img src="${item.original.imageUrl}" alt="">${item.original.text}`;
                },
                selectTemplate: function (item) {
                    return `<span class="badge" contenteditable="false" data-mentioned-${item.original.type}-id="${item.original.id}" data-value="@(${item.original.type[0]}:${item.original.id}:${item.original.text})">
                            @${item.original.text}
                        </span>`;
                },
                searchOpts: {
                    skip: true
                }
            });
        }

        if (this.hasValuesValue && this.valuesValue.length > 0) {
            tributeCollection.push({
                trigger: "*",
                values: this.valuesValue.map(v => ({value: v.Value, text: v.Text})),
                lookup: "text",
                menuItemLimit: 10,
                menuItemTemplate: function (item) {
                    return item.original.text;
                },
                selectTemplate: function (item) {
                    return `<span class="badge" contenteditable="false" data-value-id="${item.original.value}" data-value="*(${item.original.value}:${item.original.text})">
                                ☆${item.original.text}
                            </span>`;
                }
            });
        }

        if (this.hasSearchTagsUrlValue) {
            tributeCollection.push({
                trigger: "#",
                values: async (text, callback) => {
                    const urlSearchParams = new URLSearchParams();
                    urlSearchParams.append("searchText", text);
                    const response = await fetch(`${this.searchTagsUrlValue}?${urlSearchParams}`);
                    callback(await response.json());
                },
                lookup: "text",
                menuItemTemplate: function (item) {
                    return item.original.text;
                },
                selectTemplate: function (item) {
                    return `<span class="badge" contenteditable="false" data-tag="${item.original.text}" data-value="#(${item.original.text})">
                                #${item.original.text}
                            </span>`;
                },
                searchOpts: {
                    skip: true
                }
            });
        }

        if (tributeCollection.length > 0) {
            this.messageEditorTarget.tribute = new Tribute({
                collection: tributeCollection,
                containerClass: "c-feed-post-dropdown-menu",
                itemClass: "c-feed-post-dropdown-menu__item",
                allowSpaces: true
            });
            this.messageEditorTarget.tribute.attach(target);
        }
    }

    preventMultilineEntry(e) {
        if (e.keyCode === 13) {
            e.preventDefault();
        }
    }

    togglePlaceholder(e) {
        e.target.classList.toggle("has-content", e.target.textContent);
    }

    updateMessageValue() {
        let message = this.messageEditorTarget.cloneNode(true);
        message.querySelectorAll(".badge")?.forEach(b => b.replaceWith(b.dataset.value));
        this.messageTarget.value = message.textContent;
        this.messageHtmlTarget.value = this.messageEditorTarget.innerHTML;

        if (this.hasMentionedTeamIdsTarget) {
            this.mentionedTeamIdsTarget.value = [...new Set([...this.messageEditorTarget.querySelectorAll("[data-mentioned-team-id]")].map(b => b.dataset.mentionedTeamId))].join(",");
        }

        if (this.hasMentionedUserIdsTarget) {
            this.mentionedUserIdsTarget.value = [...new Set([...this.messageEditorTarget.querySelectorAll("[data-mentioned-user-id]")].map(b => b.dataset.mentionedUserId))].join(",");
        }

        if (this.hasValueIdsTarget) {
            this.valueIdsTarget.value = [...new Set([...this.messageEditorTarget.querySelectorAll("[data-value-id]")].map(b => b.dataset.valueId))].join(",");
        }

        if (this.hasTagsTarget) {
            this.tagsTarget.value = [...new Set([...this.messageEditorTarget.querySelectorAll("[data-tag]")].map(b => b.dataset.tag))].join(",");
        }
    }

    pasteText(e) {
        e.preventDefault();
        const text = e.clipboardData.getData("text");
        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        selection.deleteFromDocument();
        selection.getRangeAt(0).insertNode(document.createTextNode(text));
        selection.collapseToEnd();
        this.updateMessageValue();
        this.messageEditorTarget.classList.add("has-content");
    }

    preventDragOver(e) {
        e.preventDefault();
    }

    preventDrop(e) {
        e.preventDefault();
    }

    mentionSomeone(e) {
        e.preventDefault();
        this.showMenuForCollection("@");
    }

    addAValue(e) {
        e.preventDefault();
        this.showMenuForCollection("*");
    }

    addATag(e) {
        e.preventDefault();
        this.showMenuForCollection("#");
    }

    addAGif(e) {

        const API_KEY = 'g3pi3jckPqURR1zVYe74UfEJvI5sUfMH';
        fetch(`https://api.giphy.com/v1/gifs/trending?api_key=${API_KEY}&limit=50&rating=pg-13`)
            .then(response => response.json())
            .then(data => {
                const gifResults = this.gifResultsTarget;
                gifResults.innerHTML = '';
                data.data.forEach(gif => {
                    const img = document.createElement('img');
                    img.src = gif.images.fixed_height.url;
                    img.alt = gif.title;
                    img.setAttribute('data-still', gif.images.fixed_height_still.url);
                    img.setAttribute('data-gif', gif.images.fixed_height.url);
                    img.setAttribute('loading', 'lazy');
                    img.onclick = () => this.selectGif(gif.images.fixed_height.url, gif.images.fixed_height_still.url);
                    gifResults.appendChild(img);
                });
            })
            .catch(error => console.error('Error fetching GIFs:', error));
    }

    searchGifs() {
        const API_KEY = 'g3pi3jckPqURR1zVYe74UfEJvI5sUfMH';
        let query = this.gifSearchTarget.value;

        if (query === '') {
            // If no text in the search box fetch the trending call
            fetch(`https://api.giphy.com/v1/gifs/trending?api_key=${API_KEY}&limit=50&rating=pg-13`)
                .then(response => response.json())
                .then(data => {
                    const gifResults = this.gifResultsTarget;
                    gifResults.innerHTML = '';
                    data.data.forEach(gif => {
                        const img = document.createElement('img');
                        img.src = gif.images.fixed_height.url;
                        img.alt = gif.title;
                        img.setAttribute('data-still', gif.images.fixed_height_still.url);
                        img.setAttribute('data-gif', gif.images.fixed_height.url);
                        img.setAttribute('loading', 'lazy');
                        img.onclick = () => this.selectGif(gif.images.fixed_height.url, gif.images.fixed_height_still.url);
                        gifResults.appendChild(img);
                    });
                })
                .catch(error => console.error('Error fetching GIFs:', error));
        } else {
            // Else fetch whatever is searched for
            fetch(`https://api.giphy.com/v1/gifs/search?api_key=${API_KEY}&q=${query}&limit=50&rating=pg-13`)
                .then(response => response.json())
                .then(data => {
                    const gifResults = this.gifResultsTarget;
                    gifResults.innerHTML = '';
                    data.data.forEach(gif => {
                        const img = document.createElement('img');
                        img.src = gif.images.fixed_height.url;
                        img.alt = gif.title;
                        img.setAttribute('data-still', gif.images.fixed_height_still.url);
                        img.setAttribute('data-gif', gif.images.fixed_height.url);
                        img.setAttribute('loading', 'lazy');
                        img.onclick = () => this.selectGif(gif.images.fixed_height.url, gif.images.fixed_height_still.url);
                        gifResults.appendChild(img);
                    });
                })
                .catch(error => console.error('Error fetching GIFs:', error));

        }
    }

    searchGifsEnter(e) {
        e.preventDefault();
    }

    selectGif(url, staticURl) {
        this.clearImage();
        const selectedGifInput = this.imageUrlTarget;
        const selectedGifInputStatic = this.staticImageUrlTarget;
        selectedGifInput.value = url;
        selectedGifInputStatic.value = staticURl;

        const detailsEditorDiv = this.element.querySelector('.c-feed-post__details-gif');
        const existingGif = detailsEditorDiv.querySelector('.gif');

        if (existingGif) {
            existingGif.src = url;
        } else {
            detailsEditorDiv.insertAdjacentHTML('beforeend', `<img src="${url}" data-still="${staticURl}" alt="Selected GIF" class="gif">`);
        }

        this.gifDisplayContainerTarget.classList.remove("d-none");
        this.removeGifContainerTarget.classList.remove("d-none");
        bootstrap.Dropdown.getOrCreateInstance(this.addAGifButtonTarget).hide();
    }

    removeGif(e) {
        e.preventDefault();
        this.clearGif();
    }

    saveMessageEditorSelectionRange() {
        const selection = getSelection();

        if (this.messageEditorTarget.contains(selection.anchorNode)) {
            this.detailsEditorSelectionRange = selection.getRangeAt(0);
        } else {
            this.detailsEditorSelectionRange = document.createRange();
            this.detailsEditorSelectionRange.selectNodeContents(this.messageEditorTarget);
            this.detailsEditorSelectionRange.collapse();
        }
    }

    insertEmoji(emoji) {
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(this.detailsEditorSelectionRange);
        this.detailsEditorSelectionRange.deleteContents();
        this.detailsEditorSelectionRange.insertNode(document.createTextNode(emoji + "\u00A0"));
        this.detailsEditorSelectionRange.collapse();
        this.updateMessageValue();
        this.messageEditorTarget.classList.add("has-content");
        bootstrap.Dropdown.getOrCreateInstance(this.addAnEmojiButtonTarget).hide();
    }

    showMenuForCollection(trigger) {
        // fix for not being able to delete un-editable tribute badge when it's the first element in the message editor target
        if (!this.messageEditorTarget.classList.contains("has-content")) {
            this.messageEditorTarget.innerHTML = "<span></span>";
        }

        this.messageEditorTarget.tribute.showMenuForCollection(this.messageEditorTarget, this.messageEditorTarget.tribute.collection.findIndex(c => c.trigger === trigger));
        this.messageEditorTarget.classList.add("has-content");
    }

    imageFileChanged() {
        if (this.newImageFileTarget.files.length > 0) {
            this.clearGif();
        }

        this.renderImage();
        bootstrap.Tooltip.getInstance(this.addAnImageButtonTarget).hide();
    }

    formTargetConnected(target) {
        if (!this.hasNewImageFileTarget) {
            return;
        }

        if (!this.newImageFileTarget.form) {
            this.newImageFileTarget.value = null;
            this.newImageFileTarget.setAttribute("form", target.id);
            return;
        }

        this.renderImage();
    }

    removeImage(e) {
        e.preventDefault();
        this.clearImage();
    }

    addAnImage(e) {
        e.preventDefault();
        this.newImageFileTarget.click();
    }

    clearGif() {
        if (!this.hasImageUrlTarget) {
            return;
        }

        const selectedGifInput = this.imageUrlTarget;
        selectedGifInput.value = '';

        const detailsEditorDiv = this.element.querySelector('.c-feed-post__details-gif');
        const existingGif = detailsEditorDiv.querySelector('.gif');

        if (existingGif) {
            existingGif.remove();
        }

        this.gifDisplayContainerTarget.classList.add("d-none");
        this.removeGifContainerTarget.classList.add("d-none");
    }

    renderImage() {
        if (this.newImageFileTarget.files.length > 0) {
            const file = this.newImageFileTarget.files[0];
            const fileReader = new FileReader();
            fileReader.addEventListener(
                "load",
                _ => {
                    this.imageTarget.src = fileReader.result;
                    this.imageContainerTarget.classList.remove("d-none");
                }
            );
            fileReader.readAsDataURL(file);
        } else if (!this.hasImageFileTarget || this.imageFileTarget.value === "") {
            this.imageContainerTarget.classList.add("d-none");
            this.imageTarget.src = "";
        }
    }

    clearImage() {
        if (!this.hasNewImageFileTarget) {
            return;
        }

        this.newImageFileTarget.value = null;

        if (this.hasImageFileTarget) {
            this.imageFileTarget.value = null;
        }

        this.renderImage();
    }

    addAPoll(e) {
        e.preventDefault();
        this.clearGif();
        this.clearImage();
        this.addAGifButtonTarget.disabled = true;
        this.addAnImageButtonTarget.disabled = true;
        this.addAPollButtonTarget.disabled = true;
        this.isPollTarget.value = "True";
        this.pollContainerTarget.classList.remove("d-none");
    }

    removePoll(e) {
        e.preventDefault();
        this.pollContainerTarget.classList.add("d-none");
        this.isPollTarget.value = "False";
        this.addAPollButtonTarget.disabled = false;
        this.addAnImageButtonTarget.disabled = false;
        this.addAGifButtonTarget.disabled = false;
    }
}